import React, { useState, useEffect } from 'react';
import {
  Box,
  Typography,
  Button,
  Paper,
  Alert,
  Grid,
  Card,
  CardContent,
  TextField,
  InputAdornment,
  Accordion,
  AccordionSummary,
  AccordionDetails,
  Divider,
  Chip,
  CircularProgress,
  Dialog,
  DialogTitle,
  DialogContent,
  DialogActions,
  List,
  ListItem,
  Avatar,
  IconButton,
  Tooltip,
  Snackbar
} from '@mui/material';
import { ArrowBack as ArrowBackIcon, Download as DownloadIcon, Search as SearchIcon, ExpandMore as ExpandMoreIcon, Chat as ChatIcon, Close as CloseIcon, WhatsApp as WhatsAppIcon, ContentCopy as ContentCopyIcon } from '@mui/icons-material';
import { useNavigate, useParams } from 'react-router-dom';
import DashboardLayout from './DashboardLayout';
import * as api from '../utils/api';
import { format } from 'date-fns';

const TaskResults = () => {
  const navigate = useNavigate();
  const { id } = useParams();
  const [task, setTask] = useState(null);
  const [results, setResults] = useState(null);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState(null);
  const [searchTerm, setSearchTerm] = useState('');
  const [chatDialogOpen, setChatDialogOpen] = useState(false);
  const [selectedChatId, setSelectedChatId] = useState(null);
  const [chatMessages, setChatMessages] = useState([]);
  const [chatMessagesLoading, setChatMessagesLoading] = useState(false);
  const [whatsappDialogOpen, setWhatsappDialogOpen] = useState(false);
  const [whatsappPhone, setWhatsappPhone] = useState('');
  const [copySnackbar, setCopySnackbar] = useState({ open: false, message: '', severity: 'success' });

  useEffect(() => {
    const fetchResults = async () => {
      try {
        setLoading(true);
        const response = await api.getAnalysisTaskResults(id);
        const data = response.data.data;
        setTask(data.task_info);
        setResults(data.results);
      } catch (err) {
        console.error('Error fetching task results:', err);
        setError('Failed to load task results');
      } finally { setLoading(false); }
    };
    fetchResults();
  }, [id]);

  const getFilteredResults = () => {
    if (!results?.results || !searchTerm) return results?.results || [];
    return results.results.filter(result => JSON.stringify(result).toLowerCase().includes(searchTerm.toLowerCase()));
  };

  const exportToJSON = () => {
    const dataStr = JSON.stringify(results, null, 2);
    const dataUri = 'data:application/json;charset=utf-8,' + encodeURIComponent(dataStr);
    const exportFileDefaultName = `analysis_results_${task.id}_${new Date().toISOString().split('T')[0]}.json`;
    const linkElement = document.createElement('a');
    linkElement.setAttribute('href', dataUri);
    linkElement.setAttribute('download', exportFileDefaultName);
    linkElement.click();
  };

  const exportToCSV = () => {
    if (!results?.results) return;
    let csvContent = 'data:text/csv;charset=utf-8,';
    csvContent += 'ID,Status,Contact,Result\n';
    results.results.forEach(result => {
      const id = result.id || '';
      const status = result.status || '';
      const contact = result.metadata?.contact_name || result.metadata?.contact_phone || '';
      const resultText = JSON.stringify(result.result).replace(/"/g, '""');
      csvContent += `"${id}","${status}","${contact}","${resultText}"\n`;
    });
    const encodedUri = encodeURI(csvContent);
    const link = document.createElement('a');
    link.setAttribute('href', encodedUri);
    link.setAttribute('download', `analysis_results_${task.id}_${new Date().toISOString().split('T')[0]}.csv`);
    link.click();
  };

  const handleOpenChat = async (result) => {
    // Try to get chat_id from metadata first, then parse from result.id
    let chatId = result.metadata?.chat_id;
    
    // If not in metadata, try to extract from result.id format: dialog_{chat_id}_{contact}@c.us
    if (!chatId && result.id) {
      const match = result.id.match(/^dialog_(\d+)_/);
      if (match) {
        chatId = match[1];
      }
    }
    
    if (!chatId) {
      alert('Chat ID not found. Unable to open chat.');
      console.error('Chat ID not found. Result:', result);
      return;
    }
    
    setSelectedChatId(chatId);
    setChatDialogOpen(true);
    setChatMessagesLoading(true);
    try {
      const response = await api.getChatMessages(chatId, {
        limit: 100,
        direction: 'ASC'
      });
      setChatMessages(response.data.data || []);
    } catch (err) {
      console.error('Error fetching chat messages:', err);
      setError('Failed to load chat messages');
      setChatMessages([]);
    } finally {
      setChatMessagesLoading(false);
    }
  };

  const handleCloseChat = () => {
    setChatDialogOpen(false);
    setSelectedChatId(null);
    setChatMessages([]);
  };

  const getContactPhone = (result) => {
    // Try to get phone from metadata first
    if (result.metadata?.contact_phone) {
      return result.metadata.contact_phone;
    }
    // Try to extract from result.id format: dialog_{chat_id}_{phone}@c.us
    if (result.id) {
      const match = result.id.match(/^dialog_\d+_(\d+)@c\.us$/);
      if (match) {
        return match[1];
      }
    }
    return null;
  };

  const copyTextToClipboard = async (text) => {
    try {
      if (navigator.clipboard && navigator.clipboard.writeText) {
        await navigator.clipboard.writeText(text);
        return true;
      }

      const textarea = document.createElement('textarea');
      textarea.value = text;
      textarea.setAttribute('readonly', '');
      textarea.style.position = 'absolute';
      textarea.style.left = '-9999px';
      document.body.appendChild(textarea);
      textarea.select();
      const success = document.execCommand('copy');
      document.body.removeChild(textarea);
      return success;
    } catch (err) {
      console.error('Failed to copy text:', err);
      return false;
    }
  };

  const handleCopyPhone = async (phone) => {
    if (!phone) return;
    const normalized = String(phone).replace(/\D/g, '');
    if (!normalized) {
      setCopySnackbar({ open: true, message: 'Phone number is empty', severity: 'warning' });
      return;
    }
    const success = await copyTextToClipboard(normalized);
    setCopySnackbar({
      open: true,
      message: success ? `Number ${normalized} copied to clipboard` : 'Failed to copy number',
      severity: success ? 'success' : 'error'
    });
  };

  const handleCopySnackbarClose = (_, reason) => {
    if (reason === 'clickaway') return;
    setCopySnackbar((prev) => ({ ...prev, open: false }));
  };

  const handleOpenWhatsApp = (result) => {
    const phone = getContactPhone(result);
    if (!phone) {
      alert('Phone number not found');
      return;
    }
    setWhatsappPhone(phone);
    setWhatsappDialogOpen(true);
  };

  const renderAnalysisResult = (result) => {
    if (typeof result === 'string') return <Typography variant="body2">{result}</Typography>;
    if (typeof result === 'object') {
      return (
        <Box>
          {Object.entries(result).map(([key, value]) => (
            <Box key={key} sx={{ mb: 1 }}>
              <Typography variant="caption" color="text.secondary" component="div">{key}:</Typography>
              <Typography variant="body2" sx={{ ml: 1 }}>{Array.isArray(value) ? value.join(', ') : String(value)}</Typography>
            </Box>
          ))}
        </Box>
      );
    }
    return <Typography variant="body2">{String(result)}</Typography>;
  };

  if (loading) {
    return (
      <DashboardLayout>
        <Box sx={{ display: 'flex', justifyContent: 'center', alignItems: 'center', height: '60vh' }}>
          <CircularProgress size={60} />
        </Box>
      </DashboardLayout>
    );
  }

  if (error) {
    return (
      <DashboardLayout>
        <Alert severity="error" sx={{ mb: 3 }}>{error}</Alert>
      </DashboardLayout>
    );
  }

  const filteredResults = getFilteredResults();

  return (
    <DashboardLayout>
      <Box sx={{ mb: 4 }}>
        <Button startIcon={<ArrowBackIcon />} onClick={() => navigate('/campaign-analysis')} sx={{ mb: 2 }}>Back to task list</Button>
        <Box sx={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center' }}>
          <Box>
            <Typography variant="h4" gutterBottom>Analysis results: {task?.name}</Typography>
            <Typography variant="body1" color="text.secondary">Processed dialog results for campaign "{task?.campaign_name}"</Typography>
          </Box>
          <Box sx={{ display: 'flex', gap: 1 }}>
            <Button variant="outlined" startIcon={<DownloadIcon />} onClick={exportToJSON}>JSON</Button>
            <Button variant="outlined" startIcon={<DownloadIcon />} onClick={exportToCSV}>CSV</Button>
          </Box>
        </Box>
      </Box>

      <Grid container spacing={3}>
        <Grid item xs={12}>
          <Paper sx={{ p: 3, mb: 3 }}>
            <Typography variant="h6" gutterBottom>Summary statistics</Typography>
            <Grid container spacing={2}>
              <Grid item xs={12} sm={3}>
                <Card variant="outlined"><CardContent sx={{ textAlign: 'center' }}><Typography variant="h4" color="primary">{results?.total_items || 0}</Typography><Typography variant="body2" color="text.secondary">Total dialogs</Typography></CardContent></Card>
              </Grid>
              <Grid item xs={12} sm={3}>
                <Card variant="outlined"><CardContent sx={{ textAlign: 'center' }}><Typography variant="h4" color="success.main">{results?.completed_items || 0}</Typography><Typography variant="body2" color="text.secondary">Successfully processed</Typography></CardContent></Card>
              </Grid>
              <Grid item xs={12} sm={3}>
                <Card variant="outlined"><CardContent sx={{ textAlign: 'center' }}><Typography variant="h4" color="error.main">{results?.failed_items || 0}</Typography><Typography variant="body2" color="text.secondary">Errors</Typography></CardContent></Card>
              </Grid>
              <Grid item xs={12} sm={3}>
                <Card variant="outlined"><CardContent sx={{ textAlign: 'center' }}><Typography variant="h4" color="info.main">{task?.completed_at ? new Date(task.completed_at).toLocaleDateString() : 'N/A'}</Typography><Typography variant="body2" color="text.secondary">Completion date</Typography></CardContent></Card>
              </Grid>
            </Grid>
          </Paper>
        </Grid>

        <Grid item xs={12}>
          <Paper sx={{ p: 3 }}>
            <Box sx={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', mb: 3 }}>
              <Typography variant="h6">Dialog results ({filteredResults.length})</Typography>
              <TextField size="small" placeholder="Search results..." value={searchTerm} onChange={(e) => setSearchTerm(e.target.value)} InputProps={{ startAdornment: (<InputAdornment position="start"><SearchIcon /></InputAdornment>) }} sx={{ width: 300 }} />
            </Box>

            {filteredResults.length === 0 ? (
              <Typography variant="body1" color="text.secondary" sx={{ textAlign: 'center', py: 4 }}>No results to display</Typography>
            ) : (
              <Box sx={{ display: 'flex', flexDirection: 'column', gap: 2 }}>
                {filteredResults.map((result, index) => {
                  const contactPhone = getContactPhone(result);
                  return (
                    <Accordion key={result.id || index}>
                    <AccordionSummary expandIcon={<ExpandMoreIcon />}>
                      <Box sx={{ display: 'flex', alignItems: 'center', gap: 2, width: '100%', justifyContent: 'space-between' }}>
                        <Box sx={{ display: 'flex', alignItems: 'center', gap: 2, flex: 1 }}>
                          <Typography variant="subtitle1" sx={{ minWidth: 120 }}>{result.id}</Typography>
                          <Chip label={result.status} color={result.status === 'success' ? 'success' : 'error'} size="small" />
                          {result.metadata?.contact_name && (<Typography variant="body2" color="text.secondary"><strong>{result.metadata.contact_name}</strong></Typography>)}
                          {contactPhone && (
                            <Box sx={{ display: 'flex', alignItems: 'center', gap: 1 }}>
                              <Typography variant="body2" color="text.secondary">
                                {contactPhone}
                              </Typography>
                              <Tooltip title="Copy phone">
                                <IconButton
                                  size="small"
                                  onClick={(e) => {
                                    e.stopPropagation();
                                    handleCopyPhone(contactPhone);
                                  }}
                                >
                                  <ContentCopyIcon fontSize="small" />
                                </IconButton>
                              </Tooltip>
                            </Box>
                          )}
                        </Box>
                        <Box sx={{ display: 'flex', alignItems: 'center', gap: 1 }}>
                          {contactPhone && (
                            <Button
                              variant="outlined"
                              size="small"
                              color="success"
                              startIcon={<WhatsAppIcon />}
                              onClick={(e) => {
                                e.stopPropagation();
                                handleOpenWhatsApp(result);
                              }}
                            >
                              WhatsApp
                            </Button>
                          )}
                          <Button
                            variant="outlined"
                            size="small"
                            startIcon={<ChatIcon />}
                            onClick={(e) => {
                              e.stopPropagation();
                              handleOpenChat(result);
                            }}
                          >
                            Chat
                          </Button>
                          {result.processing_time && (<Typography variant="caption" color="text.secondary">{result.processing_time}s</Typography>)}
                        </Box>
                      </Box>
                    </AccordionSummary>
                    <AccordionDetails>
                      <Divider sx={{ mb: 2 }} />
                      {result.metadata && (
                        <Box sx={{ mb: 3 }}>
                          <Box sx={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', mb: 1 }}>
                            <Typography variant="subtitle2">Dialog metadata:</Typography>
                            <Button
                              variant="outlined"
                              size="small"
                              startIcon={<ChatIcon />}
                              onClick={() => handleOpenChat(result)}
                            >
                              Open chat
                            </Button>
                          </Box>
                          <Box sx={{ pl: 2 }}>
                            <Typography variant="body2"><strong>Chat ID:</strong> {result.metadata.chat_id}</Typography>
                            <Typography variant="body2"><strong>Instance:</strong> {result.metadata.instance_phone}</Typography>
                            <Typography variant="body2"><strong>Messages:</strong> {result.metadata.messages_count}</Typography>
                            <Typography variant="body2"><strong>Period:</strong> {new Date(result.metadata.dialog_start).toLocaleString()} - {new Date(result.metadata.dialog_end).toLocaleString()}</Typography>
                          </Box>
                        </Box>
                      )}
                      <Box>
                        <Typography variant="subtitle2" gutterBottom>Результат анализа:</Typography>
                        <Box sx={{ p: 2, backgroundColor: 'grey.50', borderRadius: 1, border: 1, borderColor: 'grey.200' }}>
                          {renderAnalysisResult(result.result)}
                        </Box>
                      </Box>
                    </AccordionDetails>
                    </Accordion>
                  );
                })}
              </Box>
            )}
          </Paper>
        </Grid>
      </Grid>

      {/* Chat Dialog Modal */}
      <Dialog
        open={chatDialogOpen}
        onClose={handleCloseChat}
        maxWidth="md"
        fullWidth
      >
        <DialogTitle>
          <Box sx={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center' }}>
            <Typography variant="h6">Chat Dialog</Typography>
            <Button onClick={handleCloseChat} size="small">
              <CloseIcon />
            </Button>
          </Box>
        </DialogTitle>
        <DialogContent>
          {chatMessagesLoading ? (
            <Box sx={{ display: 'flex', justifyContent: 'center', py: 4 }}>
              <CircularProgress />
            </Box>
          ) : chatMessages.length === 0 ? (
            <Typography variant="body2" color="text.secondary" sx={{ textAlign: 'center', py: 4 }}>
              No messages found
            </Typography>
          ) : (
            <List sx={{ maxHeight: 500, overflowY: 'auto' }}>
              {chatMessages.map((message, index) => (
                <ListItem
                  key={message.id || index}
                  sx={{
                    flexDirection: message.direction === 'outgoing' ? 'row-reverse' : 'row',
                    alignItems: 'flex-start',
                    mb: 1,
                    px: 0
                  }}
                >
                  <Avatar
                    sx={{
                      bgcolor: message.direction === 'outgoing' ? 'primary.main' : 'grey.500',
                      width: 32,
                      height: 32,
                      mr: message.direction === 'outgoing' ? 0 : 1,
                      ml: message.direction === 'outgoing' ? 1 : 0
                    }}
                  >
                    {message.direction === 'outgoing' ? 'A' : 'C'}
                  </Avatar>
                  <Box
                    sx={{
                      maxWidth: '70%',
                      bgcolor: message.direction === 'outgoing' ? 'primary.light' : 'grey.200',
                      color: message.direction === 'outgoing' ? 'primary.contrastText' : 'text.primary',
                      p: 1.5,
                      borderRadius: 2,
                      wordBreak: 'break-word'
                    }}
                  >
                    <Typography variant="body2" sx={{ mb: 0.5 }}>
                      {message.content}
                    </Typography>
                    <Typography variant="caption" sx={{ opacity: 0.7, display: 'block', textAlign: 'right' }}>
                      {message.sent_at ? format(new Date(message.sent_at), 'HH:mm') : ''}
                    </Typography>
                  </Box>
                </ListItem>
              ))}
            </List>
          )}
        </DialogContent>
        <DialogActions>
          <Button onClick={handleCloseChat}>Close</Button>
        </DialogActions>
      </Dialog>

      {/* WhatsApp Dialog Modal */}
      <Dialog
        open={whatsappDialogOpen}
        onClose={() => setWhatsappDialogOpen(false)}
        maxWidth="xs"
        fullWidth
      >
        <DialogTitle>Open WhatsApp Chat</DialogTitle>
        <DialogContent sx={{ textAlign: 'center', py: 3 }}>
          {whatsappPhone ? (
            <Box>
              <img
                src={`https://api.qrserver.com/v1/create-qr-code/?size=220x220&data=${encodeURIComponent(`https://wa.me/${String(whatsappPhone).replace(/\D/g, '')}`)}`}
                alt="Chat QR Code"
                style={{ maxWidth: '100%', border: '1px solid #ddd', borderRadius: 8 }}
              />
              <Box sx={{ mt: 2 }}>
                <Typography variant="body2" sx={{ mb: 1, color: 'text.secondary' }}>
                  Scan to open chat, or click the link below:
                </Typography>
                <a
                  href={`https://wa.me/${String(whatsappPhone).replace(/\D/g, '')}`}
                  target="_blank"
                  rel="noopener noreferrer"
                  style={{ color: '#25D366', textDecoration: 'none', wordBreak: 'break-all' }}
                >
                  https://wa.me/{String(whatsappPhone).replace(/\D/g, '')}
                </a>
              </Box>
            </Box>
          ) : (
            <Typography variant="body2">No phone number</Typography>
          )}
        </DialogContent>
        <DialogActions>
          <Button onClick={() => setWhatsappDialogOpen(false)}>Close</Button>
        </DialogActions>
      </Dialog>

      <Snackbar
        open={copySnackbar.open}
        autoHideDuration={2000}
        onClose={handleCopySnackbarClose}
        anchorOrigin={{ vertical: 'bottom', horizontal: 'right' }}
      >
        <Alert
          onClose={handleCopySnackbarClose}
          severity={copySnackbar.severity}
          variant="filled"
          sx={{ width: '100%' }}
        >
          {copySnackbar.message}
        </Alert>
      </Snackbar>
    </DashboardLayout>
  );
};

export default TaskResults;
